/**
 * Admin dashboard tab registry. Grouping rule:
 * - content:    who the avatar is and what it knows (things you edit)
 * - steering:   where the AI steers a conversation and how
 * - evaluation: scored verdicts on conversation quality
 * - debug:      raw evidence for finding out why something happened
 */
export type AdminTabGroup = "content" | "steering" | "evaluation" | "debug";

export type AdminTabKey =
  | "personas"
  | "prompts"
  | "ingestions"
  | "goals"
  | "methodology"
  | "conversation-results"
  | "feedback"
  | "graph"
  | "synapse"
  | "transcripts"
  | "errors";

export interface AdminTab {
  key: AdminTabKey;
  path: string;
  label: string;
  group: AdminTabGroup;
  superAdminOnly?: boolean;
  /** Other paths that should activate this tab. */
  aliases?: string[];
}

export const ADMIN_GROUPS: { id: AdminTabGroup; label: string }[] = [
  { id: "content", label: "Content" },
  { id: "steering", label: "Steering" },
  { id: "evaluation", label: "Evaluation" },
  { id: "debug", label: "Debug" },
];

// Order here is display order within each group.
export const ADMIN_TABS: AdminTab[] = [
  { key: "personas", path: "/admin", label: "Personas", group: "content", superAdminOnly: true },
  { key: "prompts", path: "/admin/prompts", label: "Prompt Components", group: "content", superAdminOnly: true },
  { key: "ingestions", path: "/admin/ingestions", label: "Ingestions", group: "content", superAdminOnly: true },
  { key: "goals", path: "/admin/goals", label: "Goals", group: "steering", superAdminOnly: true },
  { key: "methodology", path: "/admin/methodology-presets", label: "Methodology", group: "steering", superAdminOnly: true },
  { key: "conversation-results", path: "/admin/conversation-results", label: "Conversation Results", group: "evaluation" },
  { key: "feedback", path: "/admin/feedback", label: "User Feedback", group: "evaluation" },
  { key: "graph", path: "/admin/graph", label: "Graph Explorer", group: "debug" },
  { key: "synapse", path: "/admin/synapse", label: "User Object", group: "debug", aliases: ["/admin/synapse/demo"] },
  { key: "transcripts", path: "/admin/transcripts", label: "Transcripts", group: "debug" },
  { key: "errors", path: "/admin/errors", label: "Errors", group: "debug", superAdminOnly: true },
];

export const getVisibleTabs = (isFullAdmin: boolean): AdminTab[] =>
  ADMIN_TABS.filter((t) => isFullAdmin || !t.superAdminOnly);

/** Groups that still have at least one tab in `tabs`, in display order. */
export const getVisibleGroups = (tabs: AdminTab[]) =>
  ADMIN_GROUPS.filter((g) => tabs.some((t) => t.group === g.id));

export const findTabByPath = (tabs: AdminTab[], pathname: string): AdminTab | undefined => {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return tabs.find((t) => t.path === path || t.aliases?.includes(path));
};
